import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sendPasswordResetEmail } from 'firebase/auth';

// components
import CriticalMessage from '../../../../components/system/critical-message/CriticalMessage';
import InputBar from '../../../../components/input-bar/InputBar';
import InputsContainer from '../../../../components/inputs-container/InputsContainer';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';

// hooks
import { useSignIn } from '../../../../hooks/useSignIn';
import { selectCriticalMessage } from '../../../../store/system/system.selector.js';
import { setCriticalMessage } from '../../../../store/system/system.action.js';
import { auth } from '../../../../utils/firebase/firebase.utils';
// styles
import {
  SignInContainer,
  ForgotPasswordBtn
} from './SignIn.styles.js';

export default function SignIn() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [messageColor, setMessageColor] = useState('white');
  const { error, isPending, signIn } = useSignIn();
  const criticalMessage = useSelector(selectCriticalMessage);

  useEffect(() => {
    dispatch(setCriticalMessage(null));
  }, [dispatch]);

  const handleLogin = () => {
    dispatch(setCriticalMessage(null));
    if (!email) {
      setMessageColor('red');
      dispatch(setCriticalMessage("Please enter a valid email."));
      return;
    } else {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(email)) {
        setMessageColor('red');
        dispatch(setCriticalMessage("Please enter a valid email."));
        return;
      }
    }
    if (!password) {
      setMessageColor('red');
      dispatch(setCriticalMessage("Please enter a password."));
      return;
    }
    signIn(email, password);
  }

  useEffect(() => {
    if (error) {
      setMessageColor('red');
      // Check for Firebase error codes (v9+ format)
      if (error === "auth/user-not-found" || error === "Firebase: Error (auth/user-not-found)." || error?.includes?.("user-not-found")) {
        dispatch(setCriticalMessage("Account with that email was not found."));
        return;
      }
      if (error === "auth/invalid-login-credentials" || error === "auth/wrong-password" || error === "auth/invalid-credential" || 
          error === "Firebase: Error (auth/invalid-login-credentials)." || error?.includes?.("invalid-login-credentials") || 
          error?.includes?.("wrong-password") || error?.includes?.("invalid-credential")) {
        dispatch(setCriticalMessage("Incorrect email or password."));
        return;
      }
      if (error === "auth/invalid-email" || error?.includes?.("invalid-email")) {
        dispatch(setCriticalMessage("Invalid email address."));
        return;
      }
      if (error === "auth/too-many-requests" || error?.includes?.("too-many-requests")) {
        dispatch(setCriticalMessage("Too many failed login attempts. Please try again later."));
        return;
      }
      
      // Fallback for other errors
      dispatch(setCriticalMessage(error?.message || error || "An error occurred during sign in."));
    }

  }, [dispatch, error])

  const handleClickForgotPassword = () => {
    dispatch(setCriticalMessage(null));
    setIsForgotPasswordMode(true);
    setPassword(''); // Clear password field
  }

  const handleRememberPassword = () => {
    dispatch(setCriticalMessage(null));
    setIsForgotPasswordMode(false);
    // Email is preserved, password remains cleared
  }

  const handleSendPasswordReset = async () => {
    dispatch(setCriticalMessage(null));
    if (!email) {
      setMessageColor('red');
      dispatch(setCriticalMessage("Please enter your email address."));
      return;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(email)) {
      setMessageColor('red');
      dispatch(setCriticalMessage("Please enter a valid email address."));
      return;
    }

    setIsSendingReset(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessageColor('green');
      dispatch(setCriticalMessage("Password reset email sent! Please check your inbox."));
    } catch (err) {
      setMessageColor('red');
      if (err.code === "auth/user-not-found") {
        dispatch(setCriticalMessage("No account found with that email address."));
      } else {
        dispatch(setCriticalMessage(err.message || "Failed to send password reset email."));
      }
    } finally {
      setIsSendingReset(false);
    }
  }

  return (
    <SignInContainer>
      <InputsContainer>
        <InputBar type='email' placeholder='Enter email...' label='Email' value={email} updateValue={setEmail} />
        {!isForgotPasswordMode && (
          <InputBar type='password' placeholder={"Enter password..."} label='Password' value={password} updateValue={setPassword} showPasswordToggle />
        )}
        <PrimaryBtn
          onClick={isForgotPasswordMode ? handleSendPasswordReset : handleLogin}
          width="fill"
          title={
            isForgotPasswordMode
              ? (isSendingReset ? 'SENDING...' : 'SEND PASSWORD RESET EMAIL')
              : (isPending ? 'SIGNING IN...' : 'SIGN IN')
          }
          disabled={
            isForgotPasswordMode
              ? (isSendingReset || !email)
              : (isPending || !email || !password)
          }
        />
        {criticalMessage &&
          <div className='critical-message-container'>
            <CriticalMessage text={criticalMessage} color={messageColor} />
          </div>
        }
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <ForgotPasswordBtn onClick={isForgotPasswordMode ? handleRememberPassword : handleClickForgotPassword}>
            {isForgotPasswordMode ? "I remembered my password!" : "Forgot Password?"}
          </ForgotPasswordBtn>
        </div>

      </InputsContainer>
    </SignInContainer>
  )
}